import { GridCustomCellProps } from "@progress/kendo-react-grid";
import "./custom-cells.css";
import { useEffect, useState, useRef } from "react";
import { InputBase } from "@mui/material";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@lab/src/components/ui/alert-dialog";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { supabase } from "@/lib/supabase/supabaseClient";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useTableCellNavigation } from "./use-table-cell-navigation.hook";
import { useBulkChange } from "./use-bulk-change.hook";

export const LotCustomCell = (props: GridCustomCellProps) => {
	const [input, setInput] = useState<string>("");
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingValue, setPendingValue] = useState<string>("");
	const [originalValue, setOriginalValue] = useState<string>("");
	const isProcessingRef = useRef(false);
	const organizationId = useCentralStore((state) => state.organization)?.id;
	const articles = useCentralStore((state) => state.articles);

	const { handleChange: handleBulkChange } = useBulkChange({
		dataItem: props.dataItem,
		dataIndex: props.dataIndex,
		field: props.field,
		onChange: props.onChange,
	});

	const { handleKeyDown: handleNavigationKeyDown } = useTableCellNavigation({
		field: props.field,
		dataIndex: props.dataIndex,
		focusSelector: (targetField: string, targetRowIndex: number) =>
			`input[data-field="${targetField}"][data-row-index="${targetRowIndex}"]`,
	});

	useEffect(() => {
		if(props.field){
		const value = props.dataItem[props.field] || "";
		setInput(value);

		const articlesForCode = props.dataItem.code_e ? articles[props.dataItem.code_e] : undefined;
		const masterDataValue = articlesForCode && Object.values(articlesForCode).length > 0
			? Object.values(articlesForCode)[0].lot || ""
			: "";
		setOriginalValue(masterDataValue);
	}
	}, [props.dataItem, props.field, articles]);

	const handleChange = (newValue: string) => {
		setInput(newValue);
	};

	const applyChange = (event: React.SyntheticEvent<EventTarget, Event>, value: string) => {
		if (props.onChange) {
			props.onChange({
				dataItem: props.dataItem,
				field: props.field,
				syntheticEvent: event,
				value: value,
				dataIndex: props.dataIndex,
			});
		}
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		// Don't process if we're already handling an Enter key press
		if (isProcessingRef.current) {
			return;
		}

		const isMaterial = props.dataItem.type === JobItemTypeEnum.ARTICLE_MATERIAL;
		const isChangingValue = input !== originalValue;

		if (isMaterial && isChangingValue && input !== "") {
			// First save to job_item
			applyChange(event, input);
			setPendingValue(input);
			setShowConfirmDialog(true);
		} else {
			applyChange(event, input);
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault();
			event.stopPropagation();
			isProcessingRef.current = true;
			const isMaterial = props.dataItem.type === JobItemTypeEnum.ARTICLE_MATERIAL;
			const isChangingValue = input !== originalValue;

			if (isMaterial && isChangingValue && input !== "") {
				// First save to job_item
				applyChange(event, input);
				setPendingValue(input);
				setShowConfirmDialog(true);
			} else {
				applyChange(event, input);
				(event.target as HTMLInputElement).blur();
			}
			// Reset flag after a short delay
			setTimeout(() => {
				isProcessingRef.current = false;
			}, 100);
			return;
		}

		if (event.key === "Backspace" && event.target instanceof HTMLInputElement && event.target.value === "") {
			event.preventDefault();
			handleBulkChange("", event.metaKey || event.ctrlKey, event.shiftKey, event.altKey);
			return;
		}

		if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
			const input = event.target as HTMLInputElement;
			if (event.key === "ArrowRight") {
				const isAtEnd = input.selectionStart === input.value.length;
				if (!isAtEnd) return;
			} else {
				const isAtStart = input.selectionStart === 0;
				if (!isAtStart) return;
			}
		}

		handleNavigationKeyDown(event);
	};

	const handleConfirm = async () => {
		// Update article master data
		if (organizationId && props.dataItem.code_e) {
			const { error } = await supabase
				.from(SupabaseTableEnum.ARTICLES)
				.update({ lot: pendingValue })
				.eq('organization_id', organizationId)
				.eq('code_e', props.dataItem.code_e);

			if (error) {
				showNotification({
					message: "Fehler beim Aktualisieren der Stammdaten",
					type: "error",
				});
			} else {
				// Update global state
				const { articles } = useCentralStore.getState();
				if (articles[props.dataItem.code_e]) {
					useCentralStore.setState({
						articles: {
							...articles,
							[props.dataItem.code_e]: {
								...articles[props.dataItem.code_e],
								lot: pendingValue
							}
						}
					});
				}

				showNotification({
					message: "LOT-Nummer wurde im Auftrag und in den Stammdaten aktualisiert",
					type: "success",
				});
			}
		}

		setOriginalValue(pendingValue);
		setShowConfirmDialog(false);
		setPendingValue("");
	};

	const handleCancel = () => {
		setShowConfirmDialog(false);
		setPendingValue("");
		showNotification({
			message: "LOT-Nummer wurde nur für diesen Auftrag geändert",
			type: "info",
		});
	};

	return (
		<>
			<td className="custom-td">
				<InputBase
					data-testid={`lot-custom-cell-input-${props.field}`}
					key={props.id}
					value={input}
					onChange={(event) => {
						handleChange(event.target.value);
					}}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					fullWidth
					onFocus={(e) => e.target.select()}
					inputProps={{
						"data-field": props.field,
						"data-row-index": props.dataIndex,
					}}
					sx={{
						height: "22px",
						fontSize: "12px",
						paddingLeft: 1,
						"& input": {
							textOverflow: "ellipsis",
							padding: 0,
						},
						"&:hover": {
							backgroundColor: "rgba(0, 0, 0, 0.04)",
						},
						"&.Mui-focused": {
							backgroundColor: "rgba(0, 0, 0, 0.04)",
						},
						...props.style,
					}}
				/>
			</td>

			<AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Stammdaten aktualisieren?</AlertDialogTitle>
						<AlertDialogDescription>
							Möchten Sie die LOT-Nummer auch in den Material-Stammdaten aktualisieren?
							<br />
							<br />
							<strong>Aktuell:</strong> {originalValue || "(leer)"}
							<br />
							<strong>Neu:</strong> {pendingValue}
							<br />
							<br />
							<em>Die LOT-Nummer wurde bereits für diesen Auftrag gespeichert.</em>
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={handleCancel}>
							Nur für Auftrag
						</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirm}>
							In Stammdaten übernehmen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};